import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import {
  Tabs,
  Tab,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Autocomplete,
  Switch,
  FormControlLabel,
  Alert,
  Typography,
  CircularProgress,
  Chip,
  Grid,
  IconButton,
} from '@mui/material';
import { Delete as DeleteIcon, Info as InfoIcon, Edit as EditIcon, Close as CloseIcon, CloudUpload as CloudUploadIcon, Search as SearchIcon } from '@mui/icons-material';
import { 
  KeyboardArrowDown as KeyboardArrowDownIcon,
  KeyboardArrowUp as KeyboardArrowUpIcon
} from '@mui/icons-material';
import SightingCropImage from '../components/SightingCropImage';
import CopyToClipboardText from '../ui-components/CopyToClipboardText';
import Divider from '@mui/material/Divider';
import AddToAnomalyListModal from '../components/AddToAnomalyListModal';
import CreateGameModal from '../components/CreateGameModal';
import { useGamesCache } from '../contexts/GamesContext';

// SWR Support
import useSWR from 'swr';
import TablePagination from '@mui/material/TablePagination';








const trimImageTransparency = (imageSrc) => {
  return new Promise((resolve) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.src = imageSrc;
    img.onload = () => {
      const canvas = document.createElement("canvas");
      const ctx = canvas.getContext("2d");
      canvas.width = img.width;
      canvas.height = img.height;
      ctx.drawImage(img, 0, 0);

      const imgData = ctx.getImageData(0, 0, canvas.width, canvas.height);
      const data = imgData.data;
      
      let minX = canvas.width, minY = canvas.height, maxX = 0, maxY = 0;
      let hasAlpha = false;

      for (let y = 0; y < canvas.height; y++) {
        for (let x = 0; x < canvas.width; x++) {
          const alpha = data[(y * canvas.width + x) * 4 + 3];
          if (alpha > 0) {
            hasAlpha = true;
            if (x < minX) minX = x;
            if (y < minY) minY = y;
            if (x > maxX) maxX = x;
            if (y > maxY) maxY = y;
          }
        }
      }

      if (!hasAlpha) {
        resolve(imageSrc);
        return;
      }

      const croppedWidth = maxX - minX + 1;
      const croppedHeight = maxY - minY + 1;
      
      const croppedCanvas = document.createElement("canvas");
      const croppedCtx = croppedCanvas.getContext("2d");
      croppedCanvas.width = croppedWidth;
      croppedCanvas.height = croppedHeight;
      croppedCtx.drawImage(
        canvas,
        minX, minY, croppedWidth, croppedHeight,
        0, 0, croppedWidth, croppedHeight
      );
      
      resolve(croppedCanvas.toDataURL("image/png"));
    };
    img.onerror = () => {
      resolve(imageSrc);
    };
  });
};

const makeBackgroundTransparent = (imageSrc) => {
  return new Promise((resolve) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.src = imageSrc;
    img.onload = () => {
      const canvas = document.createElement("canvas");
      const ctx = canvas.getContext("2d");
      canvas.width = img.width;
      canvas.height = img.height;
      ctx.drawImage(img, 0, 0);

      const imgData = ctx.getImageData(0, 0, canvas.width, canvas.height);
      const data = imgData.data;

      // Read target background color from top-left pixel (0, 0)
      const targetR = data[0];
      const targetG = data[1];
      const targetB = data[2];
      const targetA = data[3];

      // If already transparent at top-left, do nothing
      if (targetA === 0) {
        resolve(imageSrc);
        return;
      }

      // Threshold for color matching (handles compression/gradient noise)
      const threshold = 20;

      for (let i = 0; i < data.length; i += 4) {
        const r = data[i];
        const g = data[i + 1];
        const b = data[i + 2];
        const a = data[i + 3];

        const diff = Math.max(
          Math.abs(r - targetR),
          Math.abs(g - targetG),
          Math.abs(b - targetB)
        );

        if (diff < threshold) {
          data[i + 3] = 0; // Make pixel fully transparent
        }
      }

      ctx.putImageData(imgData, 0, 0);
      
      // Re-crop transparent boundary margins
      const croppedCanvas = document.createElement("canvas");
      const croppedCtx = croppedCanvas.getContext("2d");
      
      let minX = canvas.width, minY = canvas.height, maxX = 0, maxY = 0;
      let hasAlpha = false;

      for (let y = 0; y < canvas.height; y++) {
        for (let x = 0; x < canvas.width; x++) {
          const alpha = data[(y * canvas.width + x) * 4 + 3];
          if (alpha > 0) {
            hasAlpha = true;
            if (x < minX) minX = x;
            if (y < minY) minY = y;
            if (x > maxX) maxX = x;
            if (y > maxY) maxY = y;
          }
        }
      }

      if (!hasAlpha) {
        resolve(canvas.toDataURL("image/png"));
        return;
      }

      const croppedWidth = maxX - minX + 1;
      const croppedHeight = maxY - minY + 1;
      croppedCanvas.width = croppedWidth;
      croppedCanvas.height = croppedHeight;
      croppedCtx.drawImage(
        canvas,
        minX, minY, croppedWidth, croppedHeight,
        0, 0, croppedWidth, croppedHeight
      );

      resolve(croppedCanvas.toDataURL("image/png"));
    };
    img.onerror = () => {
      resolve(imageSrc);
    };
  });
};

const GamesStudiosPage = () => {
  const {
    removeGameFromCache,
    updateGameInCache,
    addStudioToCache,
    updateStudioInCache,
    removeStudioFromCache,
    fetchStudios,
  } = useGamesCache();
  const [selectedTab, setSelectedTab] = useState(0);
  const [gamesByStudio, setGamesByStudio] = useState([]);
  const [gamesByStudioSearch, setGamesByStudioSearch] = useState('');
  const [gamesByStudioFilterLive, setGamesByStudioFilterLive] = useState(false);
  const [gamesByStudioFilterCrash, setGamesByStudioFilterCrash] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [dialogTitle, setDialogTitle] = useState('');
  const [loading, setLoading] = useState(true);

  // States
  const [createGameDialogOpen, setCreateGameDialogOpen] = useState(false);
  const [createStudioDialogOpen, setCreateStudioDialogOpen] = useState(false);
  const [newStudioName, setNewStudioName] = useState('');
  const [deleteStudioDialogOpen, setDeleteStudioDialogOpen] = useState(false);
  const [studioToDelete, setStudioToDelete] = useState(null);
  const [gamesAssociatedCount, setGamesAssociatedCount] = useState(0);
  const [updateStudioDialogOpen, setUpdateStudioDialogOpen] = useState(false);
  const [gameToUpdate, setGameToUpdate] = useState(null);
  const [updatedStudioID, setUpdatedStudioID] = useState('');
  const [useNewStudio, setUseNewStudio] = useState(false);
  const [gameMetadataJSON, setGameMetadataJSON] = useState('');
  const [newGameLiveDealer, setNewGameLiveDealer] = useState(false);
  const [newGameCrashGame, setNewGameCrashGame] = useState(false);
  const [createGameError, setCreateGameError] = useState('');
  const [creatingGame, setCreatingGame] = useState(false);
  const [updatedGameName, setUpdatedGameName] = useState('');
  const [editGameError, setEditGameError] = useState('');
  const gameCanvasRef = React.useRef(null);
  const [gameLogoImage, setGameLogoImage] = useState(null);
  const [zoom, setZoom] = useState(1);
  const [panX, setPanX] = useState(0);
  const [panY, setPanY] = useState(0);
  const [gameLogoPreviewUrl, setGameLogoPreviewUrl] = useState('');
  const [gameLogoFileBase64, setGameLogoFileBase64] = useState('');
  const [gameLogoContentType, setGameLogoContentType] = useState('image/png');
  const [recentGameTiles, setRecentGameTiles] = useState([]);
  const [isFetchingGameLogo, setIsFetchingGameLogo] = useState(false);
  const [editStudioDialogOpen, setEditStudioDialogOpen] = useState(false);
  const [studioToEdit, setStudioToEdit] = useState(null);
  const [updatedStudioName, setUpdatedStudioName] = useState('');
  const [editStudioError, setEditStudioError] = useState('');
  const [logoDomain, setLogoDomain] = useState('');
  const [logoPreviewUrl, setLogoPreviewUrl] = useState('');
  const [logoFileBase64, setLogoFileBase64] = useState('');
  const [logoContentType, setLogoContentType] = useState('');
  const [logoPreviewTheme, setLogoPreviewTheme] = useState('light');
  const [isFetchingLogo, setIsFetchingLogo] = useState(false);
  const [isUploadingLogo, setIsUploadingLogo] = useState(false);
  const [logoCandidates, setLogoCandidates] = useState([]);
  const [suggestedDomains, setSuggestedDomains] = useState([]);

  const [localTagOverrides, setLocalTagOverrides] = useState({});

  // Operator Association States
  const [isInHouse, setIsInHouse] = useState(false);

  const [anomalyModalOpen, setAnomalyModalOpen] = useState(false);
  const [anomalyGameId, setAnomalyGameId] = useState('');
  const [selectedOperatorID, setSelectedOperatorID] = useState('');

  // Pagination and Filtering States
  const [filterLiveDealer, setFilterLiveDealer] = useState(false);
  const [filterCrashGame, setFilterCrashGame] = useState(false);


  // Pagination States
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [studioPage, setStudioPage] = useState(0);
  const [studioRowsPerPage, setStudioRowsPerPage] = useState(25);

  // State for Game & Studio Search
  const [searchQuery, setSearchQuery] = useState('');
  const [activeSearchQuery, setActiveSearchQuery] = useState('');
  const [studioSearchQuery, setStudioSearchQuery] = useState('');
  const [pageCursors, setPageCursors] = useState([null]);
  const [selectedStudioForDialog, setSelectedStudioForDialog] = useState(null);
  const [activeGamesByStudioSearch, setActiveGamesByStudioSearch] = useState('');

  // Game Summary Modal & Fetching State
  const [summaryOpen, setSummaryOpen] = useState(false);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [summaryData, setSummaryData] = useState(null);
  const [summaryError, setSummaryError] = useState('');
  const [summaryTimescale, setSummaryTimescale] = useState('7d'); // Default to 7d
  const [summaryMetric, setSummaryMetric] = useState('prominence'); // 'prominence' or 'count'
  const [summaryTab, setSummaryTab] = useState(0);
  const [hoveredPoint, setHoveredPoint] = useState(null);
  const [summaryGameID, setSummaryGameID] = useState(null);

  useEffect(() => {
    if (!summaryOpen || !summaryGameID) return;

    const fetchSummary = async () => {
      setSummaryLoading(true);
      setSummaryError('');
      try {
        let url = `${process.env.REACT_APP_API_GAMES_STUDIOS_URL}/games/${summaryGameID}/summary`;
        const params = [];
        if (summaryTimescale === '7d') {
          params.push('days=7');
        } else if (summaryTimescale === '1m') {
          params.push('months=1');
        } else if (summaryTimescale === '3m') {
          params.push('months=3');
        }

        if (params.length > 0) {
          url += `?${params.join('&')}`;
        }

        const res = await axios.get(url, {
          headers: { 'x-api-key': process.env.REACT_APP_API_GAMES_STUDIOS_KEY }
        });
        setSummaryData(res.data);
      } catch (err) {
        console.error('Error fetching game summary:', err);
        setSummaryError(err.response?.data?.message || 'Failed to load game summary.');
      } finally {
        setSummaryLoading(false);
      }
    };

    fetchSummary();
  }, [summaryOpen, summaryGameID, summaryTimescale]);

  const handleGameNameClick = (gameID) => {
    setSummaryGameID(gameID);
    setSummaryTimescale('7d');
    setSummaryMetric('prominence');
    setSummaryTab(0);
    setSummaryOpen(true);
  };

  const fetcher = (url) =>
    axios.get(url, {
      headers: { 'x-api-key': process.env.REACT_APP_API_GAMES_STUDIOS_KEY },
    }).then(res => res.data);

  const buildGamesQuery = () => {
    let url = `${process.env.REACT_APP_API_GAMES_STUDIOS_URL}/games?limit=${rowsPerPage}`;
    if (activeSearchQuery) url += `&search=${encodeURIComponent(activeSearchQuery)}`;
    if (filterLiveDealer) url += `&liveDealer=true`;
    if (filterCrashGame) url += `&crashGame=true`;
    const cursor = pageCursors[page];
    if (cursor) url += `&cursor=${encodeURIComponent(cursor)}`;
    return url;
  };

  const buildDialogGamesQuery = () => {
    if (!openDialog || !selectedStudioForDialog) return null;
    let url = `${process.env.REACT_APP_API_GAMES_STUDIOS_URL}/games?limit=500&studioId=${selectedStudioForDialog.StudioID}`;
    if (activeGamesByStudioSearch) url += `&search=${encodeURIComponent(activeGamesByStudioSearch)}`;
    if (gamesByStudioFilterLive) url += `&liveDealer=true`;
    if (gamesByStudioFilterCrash) url += `&crashGame=true`;
    return url;
  };

  const recursiveFetcher = async (url) => {
    let allData = [];
    let currentCursor = null;
    let resourceKey = url.includes('/studios') ? 'studios' : 'games';

    do {
      const fetchUrl = currentCursor 
        ? `${url}${url.includes('?') ? '&' : '?'}cursor=${encodeURIComponent(currentCursor)}`
        : url;
      
      const res = await axios.get(fetchUrl, {
        headers: { 'x-api-key': process.env.REACT_APP_API_GAMES_STUDIOS_KEY },
      });

      const data = res.data;
      const chunk = data[resourceKey] || (Array.isArray(data) ? data : []);
      allData = [...allData, ...chunk];
      currentCursor = data.nextCursor;
    } while (currentCursor);

    return { [resourceKey]: allData };
  };

  const { data: gamesData, isLoading: gamesLoading, mutate: mutateGames } = useSWR(
    buildGamesQuery,
    fetcher
  );

  const { data: dialogGamesData, isLoading: dialogGamesLoading } = useSWR(
    buildDialogGamesQuery,
    fetcher
  );

  const { data: studiosData, isLoading: studiosLoading, mutate: mutateStudios } = useSWR(
    `${process.env.REACT_APP_API_GAMES_STUDIOS_URL}/studios?limit=1000`,
    recursiveFetcher
  );

  const { data: operatorsData } = useSWR(
    `${process.env.REACT_APP_API_GAMES_STUDIOS_URL}/operators`,
    fetcher
  );

  const games = gamesData?.games || [];
  const nextCursor = gamesData?.nextCursor || null;
  const filteredGamesByStudioList = dialogGamesData?.games || [];
  const studios = studiosData?.studios || [];
  const operators = operatorsData?.operators || [];

  // Track nextCursor when data arrives
  useEffect(() => {
    if (nextCursor) {
        setPageCursors(prev => {
            const newCursors = [...prev];
            newCursors[page + 1] = nextCursor;
            return newCursors;
        });
    }
  }, [nextCursor, page]);

  // When filters change, reset pagination
  useEffect(() => {
     setPage(0);
     setPageCursors([null]);
  }, [activeSearchQuery, filterLiveDealer, filterCrashGame, rowsPerPage]);



  const handleTabChange = (event, newValue) => {
    setSelectedTab(newValue);
  };

  const openCreateGameDialog = () => {
    setCreateGameDialogOpen(true);
  };

  const handleGameCreated = (newGameData) => {
    mutateGames();
    mutateStudios(); // in case a new studio was created
  };

  const handleDeleteGame = async (gameId) => {
    if (!window.confirm('Are you sure you want to delete this game?')) return;

    // Optimistically update local SWR cache before actual request
    await mutateGames(
      async (current) => {
        try {
          await axios.delete(`${process.env.REACT_APP_API_GAMES_STUDIOS_URL}/games`, {
            headers: {
              'x-api-key': process.env.REACT_APP_API_GAMES_STUDIOS_KEY,
            },
            data: { GameID: gameId },
          });
          removeGameFromCache(gameId);
          // Return updated cache without the deleted game
          return {
            ...current,
            games: current.games.filter((game) => game.GameID !== gameId),
          };
        } catch (error) {
          console.error('Error deleting game:', error);
          throw error; // rethrow to trigger fallback revalidation
        }
      },
      {
        optimisticData: (current) => ({
          ...current,
          games: current.games.filter((game) => game.GameID !== gameId),
        }),
        rollbackOnError: true,
        revalidate: false, // prevent unnecessary refetch
      }
    );
  };


  const handleOpenDialog = (studio) => {
    setSelectedStudioForDialog(studio);
    setDialogTitle(`Games by ${studio.Name}`);
    setGamesByStudioSearch('');
    setActiveGamesByStudioSearch('');
    setGamesByStudioFilterLive(false);
    setGamesByStudioFilterCrash(false);
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
  };

  // Handle Create Studio
  const handleCreateStudio = async () => {
    try {
      // Send the POST request to create the new studio
      const res = await axios.post(
        `${process.env.REACT_APP_API_GAMES_STUDIOS_URL}/studios`,
        { Name: newStudioName },
        {
          headers: {
            'x-api-key': process.env.REACT_APP_API_GAMES_STUDIOS_KEY,
          },
        }
      );

      const studioId = res.data?.StudioID || res.data?.studio_id;
      if (studioId) {
        addStudioToCache({ StudioID: studioId, Name: newStudioName });
      } else {
        fetchStudios(true);
      }

      // Trigger SWR to re-fetch the updated list of studios
      await mutateStudios();

      // Close the dialog and reset input
      setCreateStudioDialogOpen(false);
      setNewStudioName('');
    } catch (error) {
      console.error('Error creating studio:', error);
      alert('Failed to create studio. Please try again.');
    }
  };


  // Handle Delete Studio
  const handleDeleteStudio = (studio) => {
    const associatedGames = games.filter((game) => game.StudioID === studio.StudioID);
    setGamesAssociatedCount(associatedGames.length);
    setStudioToDelete(studio);
    setDeleteStudioDialogOpen(true);
  };

  const confirmDeleteStudio = async () => {


    if (!studioToDelete) return;

    const studioId = studioToDelete.StudioID;

    const associatedGames = games.filter((game) => game.StudioID === studioId);
    if (associatedGames.length > 0) {
      alert(`You must reassign all ${associatedGames.length} games before deleting this studio.`);
      return;
    }

    // Optimistically update both studios and games
    try {
      await Promise.all([
        mutateStudios(
          async (current) => {
            await axios.delete(`${process.env.REACT_APP_API_GAMES_STUDIOS_URL}/studios`, {
              headers: {
                'x-api-key': process.env.REACT_APP_API_GAMES_STUDIOS_KEY,
              },
              data: { StudioID: studioId },
            });
            return {
              ...current,
              studios: current.studios.filter((studio) => studio.StudioID !== studioId),
            };
          },
          {
            optimisticData: (current) => ({
              ...current,
              studios: current.studios.filter((studio) => studio.StudioID !== studioId),
            }),
            rollbackOnError: true,
            revalidate: false,
          }
        ),
        mutateGames(
          async (current) => ({
            ...current,
            games: current.games.map((game) =>
              game.StudioID === studioId ? { ...game, StudioID: 'unknown' } : game
            ),
          }),
          {
            optimisticData: (current) => ({
              ...current,
              games: current.games.map((game) =>
                game.StudioID === studioId ? { ...game, StudioID: 'unknown' } : game
              ),
            }),
            rollbackOnError: false, // No need to rollback games; safe default
            revalidate: false,
          }
        ),
      ]);

      removeStudioFromCache(studioId);
      setDeleteStudioDialogOpen(false);
      setStudioToDelete(null);
    } catch (error) {
      console.error("Error deleting studio:", error);
      alert("Failed to delete studio. Please try again.");
    }
  };


  useEffect(() => {
    if (!gameLogoImage) return;
    const canvasWidth = 400;
    const canvasHeight = 300;
    const zoomMin = Math.max(canvasWidth / gameLogoImage.width, canvasHeight / gameLogoImage.height);
    setZoom(zoomMin);
    setPanX(0);
    setPanY(0);
  }, [gameLogoImage]);

  useEffect(() => {
    const canvas = gameCanvasRef.current;
    if (!canvas || !gameLogoImage) return;
    const ctx = canvas.getContext('2d');
    const canvasWidth = 400;
    const canvasHeight = 300;
    
    ctx.clearRect(0, 0, canvasWidth, canvasHeight);
    ctx.save();
    ctx.translate(canvasWidth / 2, canvasHeight / 2);
    ctx.scale(zoom, zoom);
    ctx.drawImage(
      gameLogoImage,
      -gameLogoImage.width / 2 + panX,
      -gameLogoImage.height / 2 + panY
    );
    ctx.restore();
    
    ctx.strokeStyle = '#cccccc';
    ctx.lineWidth = 1;
    ctx.strokeRect(0, 0, canvasWidth, canvasHeight);
  }, [gameLogoImage, zoom, panX, panY]);

  // Handle Edit Game (Name and Studio)
  const handleUpdateStudio = async (game) => {
    setGameToUpdate(game);
    setUpdatedGameName(game.Name);
    setUpdatedStudioID(game.StudioID);
    setEditGameError('');
    setGameLogoImage(null);
    if (game.LogoPath) {
      setGameLogoPreviewUrl(`https://reports.productradars.com/${game.LogoPath}?t=${game.LogoUpdatedAt || ''}`);
    } else {
      setGameLogoPreviewUrl('');
    }
    setGameLogoFileBase64('');
    setRecentGameTiles([]);
    setUpdateStudioDialogOpen(true);

    try {
      const summaryRes = await axios.get(
        `${process.env.REACT_APP_API_GAMES_STUDIOS_URL}/games/${game.GameID}/summary?minWidth=180&minHeight=135`,
        { headers: { 'x-api-key': process.env.REACT_APP_API_GAMES_STUDIOS_KEY } }
      );
      setRecentGameTiles(summaryRes.data.RecentTiles || []);
    } catch (err) {
      console.error("Failed to load game summary recent tiles:", err);
    }
  };

  const confirmUpdateStudio = async () => {
    if (!gameToUpdate) return;
    if (!updatedGameName.trim()) {
      setEditGameError('Game name cannot be empty.');
      return;
    }

    setIsUploadingLogo(true);
    try {
      const patchData = {
        GameID: gameToUpdate.GameID,
        Name: updatedGameName,
        StudioID: updatedStudioID,
      };

      if (gameLogoPreviewUrl === '') {
        patchData.LogoPath = '';
      }

      await axios.patch(
        `${process.env.REACT_APP_API_GAMES_STUDIOS_URL}/games`,
        patchData,
        {
          headers: { 'x-api-key': process.env.REACT_APP_API_GAMES_STUDIOS_KEY },
        }
      );
      updateGameInCache(patchData);

      if (gameLogoImage && gameCanvasRef.current) {
        const base64Image = gameCanvasRef.current.toDataURL('image/png');
        await axios.post(
          `${process.env.REACT_APP_API_GAMES_STUDIOS_URL}/games/${gameToUpdate.GameID}/logo`,
          {
            ImageBase64: base64Image,
            ContentType: 'image/png'
          },
          {
            headers: { 
              'x-api-key': process.env.REACT_APP_API_GAMES_STUDIOS_KEY,
              'Content-Type': 'application/json'
            }
          }
        );
      }

      setUpdateStudioDialogOpen(false);
      setGameToUpdate(null);
      await mutateGames();
    } catch (error) {
      console.error('Error updating game:', error);
      const errorMessage = error.response?.data?.message || (typeof error.response?.data === 'string' ? error.response.data : 'Failed to update game.');
      setEditGameError(errorMessage);
    } finally {
      setIsUploadingLogo(false);
    }
  };

  const handleLocalGameLogoUpload = (event) => {
    const file = event.target.files[0];
    if (!file) return;
    
    const reader = new FileReader();
    reader.onload = (e) => {
      const base64 = e.target.result;
      const img = new Image();
      img.src = base64;
      img.onload = () => {
        setGameLogoImage(img);
        setGameLogoContentType(file.type);
        setGameLogoPreviewUrl(base64);
      };
    };
    reader.readAsDataURL(file);
  };

  const handleEditStudio = async (studio) => {
    setStudioToEdit(studio);
    setUpdatedStudioName(studio.Name);
    const opId = studio.OperatorID || '';
    setIsInHouse(!!opId);
    setSelectedOperatorID(opId);
    if (studio.LogoPath) {
      setLogoPreviewUrl(`https://reports.productradars.com/${studio.LogoPath}?t=${studio.LogoUpdatedAt || ''}`);
    } else {
      setLogoPreviewUrl('');
    }
    setLogoFileBase64('');
    setLogoContentType('');
    setLogoCandidates([]);
    setSuggestedDomains([]);
    const guessed = studio.Name.toLowerCase()
      .trim()
      .replace(/[^a-z0-9]/g, '') + '.com';
    setLogoDomain(guessed);
    setEditStudioDialogOpen(true);
    setEditStudioError('');

    // Fetch domain suggestions from Clearbit Autocomplete API in background
    try {
      const query = encodeURIComponent(studio.Name);
      const res = await axios.get(`https://autocomplete.clearbit.com/v1/companies/suggest?query=${query}`);
      if (res.data && Array.isArray(res.data)) {
        const domains = res.data
          .map(item => item.domain)
          .filter((domain, index, self) => domain && self.indexOf(domain) === index);
        setSuggestedDomains(domains);
      }
    } catch (err) {
      console.warn("Failed to fetch domain suggestions from Clearbit Autocomplete:", err);
    }
  };

  const confirmEditStudio = async () => {
    if (!studioToEdit) return;
    if (!updatedStudioName.trim()) {
      setEditStudioError('Studio name cannot be empty.');
      return;
    }
    setIsUploadingLogo(true);
    try {
      await axios.patch(
        `${process.env.REACT_APP_API_GAMES_STUDIOS_URL}/studios`,
        {
          StudioID: studioToEdit.StudioID,
          Name: updatedStudioName,
          OperatorID: isInHouse ? selectedOperatorID : "",
        },
        {
          headers: { 'x-api-key': process.env.REACT_APP_API_GAMES_STUDIOS_KEY },
        }
      );

      updateStudioInCache({
        StudioID: studioToEdit.StudioID,
        Name: updatedStudioName,
        OperatorID: isInHouse ? selectedOperatorID : "",
      });

      if (logoFileBase64) {
        await axios.post(
          `${process.env.REACT_APP_API_GAMES_STUDIOS_URL}/studios/${studioToEdit.StudioID}/logo`,
          {
            ImageBase64: logoFileBase64,
            ContentType: logoContentType
          },
          {
            headers: { 
              'x-api-key': process.env.REACT_APP_API_GAMES_STUDIOS_KEY,
              'Content-Type': 'application/json'
            }
          }
        );
      } else if (logoPreviewUrl === '') {
        await axios.patch(
          `${process.env.REACT_APP_API_GAMES_STUDIOS_URL}/studios`,
          {
            StudioID: studioToEdit.StudioID,
            LogoPath: ""
          },
          {
            headers: { 'x-api-key': process.env.REACT_APP_API_GAMES_STUDIOS_KEY },
          }
        );
      }

      setEditStudioDialogOpen(false);
      setStudioToEdit(null);
      await mutateStudios();
      await mutateGames();
    } catch (error) {
      console.error('Error updating studio:', error);
      const errorMessage = error.response?.data?.message || (typeof error.response?.data === 'string' ? error.response.data : 'Failed to update studio.');
      setEditStudioError(errorMessage);
    } finally {
      setIsUploadingLogo(false);
    }
  };

  const handleFetchLogoOnline = async () => {
    await fetchLogoForDomain(logoDomain);
  };

  const fetchLogoForDomain = async (domainToFetch) => {
    if (!domainToFetch.trim()) return;
    setIsFetchingLogo(true);
    setEditStudioError('');
    setLogoCandidates([]);
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_GAMES_STUDIOS_URL}/studios/suggest-logos`,
        {
          params: { domain: domainToFetch.trim() },
          headers: { 'x-api-key': process.env.REACT_APP_API_GAMES_STUDIOS_KEY }
        }
      );
      
      const candidates = response.data.candidates || [];
      setLogoCandidates(candidates);
      
      if (candidates.length > 0) {
        await handleSelectCandidate(candidates[0]);
      } else {
        setEditStudioError("No logo candidates found for this domain.");
      }
    } catch (err) {
      console.error("Failed to suggest logos:", err);
      setEditStudioError("Error searching logo candidates. Make sure the domain is valid.");
    } finally {
      setIsFetchingLogo(false);
    }
  };

  const handleSelectCandidate = async (candidateUrl) => {
    setIsFetchingLogo(true);
    setEditStudioError('');
    try {
      let contentType = 'image/png';
      if (candidateUrl.toLowerCase().endsWith('.svg')) {
        contentType = 'image/svg+xml';
      } else if (candidateUrl.toLowerCase().endsWith('.jpg') || candidateUrl.toLowerCase().endsWith('.jpeg')) {
        contentType = 'image/jpeg';
      }

      // Fetch via backend proxy to bypass CORS
      const response = await axios.get(
        `${process.env.REACT_APP_API_GAMES_STUDIOS_URL}/studios/suggest-logos`,
        {
          params: { url: candidateUrl },
          headers: { 'x-api-key': process.env.REACT_APP_API_GAMES_STUDIOS_KEY }
        }
      );

      const { base64, contentType: responseType } = response.data;
      if (responseType === 'image/svg+xml' || candidateUrl.toLowerCase().endsWith('.svg')) {
        setLogoFileBase64(base64);
        setLogoContentType('image/svg+xml');
        setLogoPreviewUrl(base64);
      } else {
        const trimmed = await trimImageTransparency(base64);
        setLogoFileBase64(trimmed);
        setLogoContentType(responseType || contentType);
        setLogoPreviewUrl(trimmed);
      }
    } catch (err) {
      console.error("Error selecting logo candidate:", err);
      setEditStudioError("Failed to fetch this logo option due to backend network/CORS restrictions.");
    } finally {
      setIsFetchingLogo(false);
    }
  };

  const handleLocalLogoUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;
    
    const reader = new FileReader();
    reader.onload = async (e) => {
      const base64 = e.target.result;
      
      if (file.type === 'image/svg+xml') {
        setLogoFileBase64(base64);
        setLogoContentType(file.type);
        setLogoPreviewUrl(base64);
      } else {
        const trimmed = await trimImageTransparency(base64);
        setLogoFileBase64(trimmed);
        setLogoContentType(file.type);
        setLogoPreviewUrl(trimmed);
      }
    };
    reader.readAsDataURL(file);
  };

  const handleTagToggle = (gameId, tagName, newValue) => {
    // Apply immediate UI change
    setLocalTagOverrides((prev) => ({
      ...prev,
      [`${gameId}.${tagName}`]: newValue,
    }));
    updateGameInCache({ GameID: gameId, [tagName]: newValue });

    // Fire-and-forget API call
    axios.patch(
      `${process.env.REACT_APP_API_GAMES_STUDIOS_URL}/games`,
      {
        GameID: gameId,
        [tagName]: newValue,
      },
      {
        headers: {
          'x-api-key': process.env.REACT_APP_API_GAMES_STUDIOS_KEY,
        },
      }
    ).catch((error) => {
      console.error(`Failed to update tag ${tagName}:`, error);
      alert(`Update failed. Rolling back.`);
      updateGameInCache({ GameID: gameId, [tagName]: !newValue });

      // Roll back visual state if it fails
      setLocalTagOverrides((prev) => {
        const copy = { ...prev };
        delete copy[`${gameId}.${tagName}`];
        return copy;
      });
    });
  };

  // Server-side paginates and filters games now
  const paginatedGames = games;

  const filteredStudios = studios
    .filter((studio) =>
      (studio.Name || '').toLowerCase().includes(studioSearchQuery.toLowerCase())
    )
    .sort((a, b) => (a.Name || '').localeCompare(b.Name || ''));

  const paginatedStudios = filteredStudios.slice(
    studioPage * studioRowsPerPage,
    (studioPage + 1) * studioRowsPerPage
  );



  const renderTrendChart = () => {
    if (!summaryData || !summaryData.History || summaryData.History.length === 0) {
      return (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: 220, bgcolor: 'action.hover', borderRadius: 2, p: 2, width: '100%' }}>
          <Typography variant="body2" color="text.secondary">No sighting history recorded in the selected timescale.</Typography>
        </Box>
      );
    }

    const history = summaryData.History;
    const metric = summaryMetric;

    const width = 500;
    const height = 180;
    const paddingLeft = 40;
    const paddingRight = 10;
    const paddingTop = 15;
    const paddingBottom = 30;

    const chartWidth = width - paddingLeft - paddingRight;
    const chartHeight = height - paddingTop - paddingBottom;

    const values = history.map(h => h[metric === 'prominence' ? 'Prominence' : 'Count']);
    const maxVal = Math.max(...values, metric === 'prominence' ? 100 : 1);
    const minVal = 0;

    const getX = (index) => {
      if (history.length <= 1) return paddingLeft + chartWidth / 2;
      return paddingLeft + (index / (history.length - 1)) * chartWidth;
    };

    const getY = (val) => {
      const denom = maxVal - minVal;
      if (denom === 0) return paddingTop + chartHeight / 2;
      return paddingTop + chartHeight - ((val - minVal) / denom) * chartHeight;
    };

    let linePath = '';
    let areaPath = '';

    history.forEach((pt, idx) => {
      const val = pt[metric === 'prominence' ? 'Prominence' : 'Count'];
      const x = getX(idx);
      const y = getY(val);

      if (idx === 0) {
        linePath = `M ${x} ${y}`;
        areaPath = `M ${x} ${paddingTop + chartHeight} L ${x} ${y}`;
      } else {
        linePath += ` L ${x} ${y}`;
        areaPath += ` L ${x} ${y}`;
      }

      if (idx === history.length - 1) {
        areaPath += ` L ${x} ${paddingTop + chartHeight} Z`;
      }
    });

    const yGridValues = metric === 'prominence'
      ? [0, 25, 50, 75, 100]
      : Array.from({ length: 4 }, (_, i) => Math.round(minVal + (i * (maxVal - minVal)) / 3));

    const xLabels = [];
    if (history.length > 0) {
      xLabels.push({ text: history[0].Date, idx: 0 });
      if (history.length > 2) {
        xLabels.push({ text: history[Math.floor(history.length / 2)].Date, idx: Math.floor(history.length / 2) });
      }
      if (history.length > 1) {
        xLabels.push({ text: history[history.length - 1].Date, idx: history.length - 1 });
      }
    }

    return (
      <Box sx={{ position: 'relative', width: '100%' }}>
        <svg viewBox={`0 0 ${width} ${height}`} width="100%" height={height} style={{ overflow: 'visible' }}>
          <defs>
            <linearGradient id="chartGradient" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#1976d2" stopOpacity={0.4} />
              <stop offset="100%" stopColor="#1976d2" stopOpacity={0.0} />
            </linearGradient>
          </defs>

          {yGridValues.map((val, i) => {
            const y = getY(val);
            return (
              <g key={i}>
                <line
                  x1={paddingLeft}
                  y1={y}
                  x2={width - paddingRight}
                  y2={y}
                  stroke="#e0e0e0"
                  strokeDasharray="4 4"
                />
                <text
                  x={paddingLeft - 8}
                  y={y + 4}
                  fontSize="9"
                  fill="#757575"
                  textAnchor="end"
                >
                  {val}
                </text>
              </g>
            );
          })}

          {history.length > 1 && (
            <path d={areaPath} fill="url(#chartGradient)" />
          )}

          {history.length > 1 ? (
            <path
              d={linePath}
              fill="none"
              stroke="#1976d2"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          ) : (
            history.length === 1 && (
              <circle cx={getX(0)} cy={getY(history[0][metric === 'prominence' ? 'Prominence' : 'Count'])} r={4} fill="#1976d2" />
            )
          )}

          {xLabels.map((lbl, idx) => {
            const x = getX(lbl.idx);
            let textAnchor = 'middle';
            if (lbl.idx === 0) textAnchor = 'start';
            if (lbl.idx === history.length - 1) textAnchor = 'end';
            return (
              <text
                key={idx}
                x={x}
                y={height - 8}
                fontSize="9"
                fill="#757575"
                textAnchor={textAnchor}
              >
                {lbl.text}
              </text>
            );
          })}

          {history.map((pt, i) => {
            const val = pt[metric === 'prominence' ? 'Prominence' : 'Count'];
            const x = getX(i);
            const y = getY(val);
            const nextX = getX(i + 1);
            const prevX = getX(i - 1);
            const rectWidth = history.length <= 1
              ? chartWidth
              : (i === 0 ? (nextX - x) : (i === history.length - 1 ? (x - prevX) : (nextX - prevX) / 2));
            const rectX = history.length <= 1
              ? paddingLeft
              : (i === 0 ? x : x - rectWidth / 2);

            return (
              <rect
                key={i}
                x={rectX}
                y={paddingTop}
                width={Math.max(1, rectWidth)}
                height={chartHeight}
                fill="transparent"
                style={{ cursor: 'pointer' }}
                onMouseEnter={() => setHoveredPoint({ ...pt, x, y, displayVal: val })}
                onMouseLeave={() => setHoveredPoint(null)}
              />
            );
          })}

          {hoveredPoint && (
            <circle
              cx={hoveredPoint.x}
              cy={hoveredPoint.y}
              r={5}
              fill="#1976d2"
              stroke="#ffffff"
              strokeWidth="2"
              style={{ pointerEvents: 'none' }}
            />
          )}
        </svg>

        {hoveredPoint && (
          <Paper
            elevation={3}
            sx={{
              position: 'absolute',
              top: Math.max(0, hoveredPoint.y - 65),
              left: Math.min(width - 120, Math.max(paddingLeft, hoveredPoint.x - 60)),
              padding: '6px 10px',
              backgroundColor: 'rgba(33, 33, 33, 0.95)',
              color: '#ffffff',
              borderRadius: 1.5,
              pointerEvents: 'none',
              zIndex: 10,
              minWidth: 100
            }}
          >
            <Typography variant="caption" display="block" sx={{ fontWeight: 'bold', color: '#b0bec5' }}>
              {hoveredPoint.Date}
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
              {metric === 'prominence' ? `Prominence: ${hoveredPoint.displayVal}%` : `Appearances: ${hoveredPoint.displayVal}`}
            </Typography>
          </Paper>
        )}
      </Box>
    );
  };

  if (gamesLoading || studiosLoading) {
    return <div>Loading...</div>;
  }

  return (
    <Box sx={{ width: '100%' }}>
      <Tabs value={selectedTab} onChange={handleTabChange} centered>
        <Tab label="Games" />
        <Tab label="Studios" />
      </Tabs>

      {selectedTab === 0 && (
        <Box>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', my: 2 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={filterLiveDealer}
                  onChange={(e) => setFilterLiveDealer(e.target.checked)}
                />
              }
              label="Live Dealer"
            />
            <FormControlLabel
              control={
                <Switch
                  checked={filterCrashGame}
                  onChange={(e) => setFilterCrashGame(e.target.checked)}
                />
              }
              label="Crash Game"
            />
          </Box>

          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
              <TextField
                label="Search games"
                variant="outlined"
                size="small"
                placeholder="Search..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    setActiveSearchQuery(searchQuery);
                    setPage(0);
                  }
                }}
              />
              <Button 
                variant="contained" 
                color="secondary" 
                onClick={() => {
                  setActiveSearchQuery(searchQuery);
                  setPage(0);
                }}
              >
                Search
              </Button>
            </Box>
            <Button variant="contained" color="primary" onClick={openCreateGameDialog}>
              CREATE GAME
            </Button>
          </Box>

          <TableContainer component={Paper} sx={{ marginTop: 2 }}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Game Name</TableCell>
                  <TableCell>Live Dealer</TableCell>
                  <TableCell>Crash Game</TableCell>
                  <TableCell>Studio Name</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedGames.map((game) => (
                  <TableRow key={game.GameID}>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        {game.LogoPath && (
                          <Box 
                            component="img"
                            src={`https://reports.productradars.com/${game.LogoPath}?t=${game.LogoUpdatedAt || ''}`}
                            alt={`${game.Name} logo`}
                            sx={{ 
                              height: 30, 
                              width: 40,
                              objectFit: 'contain',
                              bgcolor: '#f9f9f9', 
                              p: 0.5,
                              borderRadius: 1,
                              border: '1px solid',
                              borderColor: 'divider'
                            }}
                          />
                        )}
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 0.25 }}>
                          <Typography
                            variant="body2"
                            sx={{
                              cursor: 'pointer',
                              fontWeight: 'bold',
                              color: 'primary.main',
                              '&:hover': { textDecoration: 'underline' }
                            }}
                            onClick={() => handleGameNameClick(game.GameID)}
                          >
                            {game.Name}
                          </Typography>
                          <CopyToClipboardText text={game.GameID} />
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={
                              localTagOverrides[`${game.GameID}.LiveDealer`] ??
                              !!game.LiveDealer
                            }
                            onChange={(e) =>
                              handleTagToggle(game.GameID, "LiveDealer", e.target.checked)
                            }
                          />

                        }
                        label=""
                      />
                    </TableCell>
                    <TableCell>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={
                              localTagOverrides[`${game.GameID}.CrashGame`] ??
                              !!game.CrashGame
                            }
                            onChange={(e) =>
                              handleTagToggle(game.GameID, "CrashGame", e.target.checked)
                            }
                          />
                        }
                        label=""
                      />
                    </TableCell>

                    <TableCell>
                      {studios.find((studio) => studio.StudioID === game.StudioID)?.Name || 'Unknown'}
                    </TableCell>
                    <TableCell>
                      <Button
                        variant="contained"
                        color="secondary"
                        startIcon={<DeleteIcon />}
                        onClick={() => handleDeleteGame(game.GameID)}
                      >
                        Delete
                      </Button>
                      <Button
                        variant="contained"
                        color="primary"
                        startIcon={<EditIcon />}
                        onClick={() => handleUpdateStudio(game)}
                        sx={{ marginLeft: 2 }}
                      >
                        Edit
                      </Button>
                      <Button
                        variant="outlined"
                        color="warning"
                        onClick={() => {
                          setAnomalyGameId(game.GameID);
                          setAnomalyModalOpen(true);
                        }}
                        sx={{ marginLeft: 2 }}
                      >
                        Flag
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={nextCursor ? -1 : (page * rowsPerPage + games.length)}
            page={page}
            onPageChange={(event, newPage) => setPage(newPage)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(event) => {
              setRowsPerPage(parseInt(event.target.value, 10));
              setPage(0);
            }}
            rowsPerPageOptions={[10, 25, 50, 100]}
          />

        </Box>
      )}

      {selectedTab === 1 && (
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, my: 2, flexWrap: 'wrap' }}>
            <TextField
              label="Search studios"
              variant="outlined"
              size="small"
              placeholder="Type a studio name..."
              value={studioSearchQuery}
              onChange={(e) => {
                setStudioSearchQuery(e.target.value);
                setStudioPage(0);
              }}
              sx={{ minWidth: 250 }}
            />

            <Button
              variant="contained"
              color="primary"
              onClick={() => setCreateStudioDialogOpen(true)}
            >
              CREATE STUDIO
            </Button>
          </Box>
          <TableContainer component={Paper} sx={{ marginTop: 2 }}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Studio Name</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedStudios.map((studio) => (
                  <TableRow key={studio.StudioID}>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        {studio.LogoPath && (
                          <Box 
                            component="img"
                            src={`https://reports.productradars.com/${studio.LogoPath}?t=${studio.LogoUpdatedAt || ''}`}
                            alt={`${studio.Name} logo`}
                            sx={{ 
                              height: 30, 
                              width: 60, 
                              objectFit: 'contain',
                              bgcolor: '#f9f9f9', 
                              p: 0.5,
                              borderRadius: 1,
                              border: '1px solid',
                              borderColor: 'divider'
                            }}
                          />
                        )}
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 0.25 }}>
                          <Typography variant="body2" sx={{ fontWeight: 'bold' }}>{studio.Name}</Typography>
                          <CopyToClipboardText text={studio.StudioID} />
                          {studio.OperatorID && (
                            <Chip 
                              label={`In-house: ${operators.find(op => op.OperatorID === studio.OperatorID)?.OperatorName || 'Unknown Operator'}`}
                              size="small"
                              variant="outlined"
                              sx={{ mt: 0.5, fontSize: '0.75rem' }}
                            />
                          )}
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Button
                        variant="contained"
                        color="primary"
                        startIcon={<InfoIcon />}
                        onClick={() => handleOpenDialog(studio)}
                      >
                        View Games
                      </Button>
                      <Button
                        variant="contained"
                        color="primary"
                        startIcon={<EditIcon />}
                        onClick={() => handleEditStudio(studio)}
                        sx={{ marginLeft: 2 }}
                      >
                        Edit
                      </Button>
                      <Button
                        variant="contained"
                        color="secondary"
                        startIcon={<DeleteIcon />}
                        onClick={() => handleDeleteStudio(studio)}
                        sx={{ marginLeft: 2 }}
                      >
                        Delete
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={filteredStudios.length}
            page={studioPage}
            onPageChange={(event, newPage) => setStudioPage(newPage)}
            rowsPerPage={studioRowsPerPage}
            onRowsPerPageChange={(event) => {
              setStudioRowsPerPage(parseInt(event.target.value, 10));
              setStudioPage(0);
            }}
            rowsPerPageOptions={[10, 25, 50, 100]}
          />
        </Box>
      )}

      {/* Dialog for viewing games by studio */}
      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        fullWidth
        maxWidth="md"
        PaperProps={{ sx: { minHeight: 400 } }}
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          {dialogTitle}
          <Box component="span" sx={{ fontSize: 14, color: 'text.secondary' }}>
            Total Games: {filteredGamesByStudioList.length}
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2, alignItems: 'center' }}>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flex: 1, minWidth: 220 }}>
              <TextField
                label="Search games"
                variant="outlined"
                size="small"
                fullWidth
                value={gamesByStudioSearch}
                onChange={(e) => setGamesByStudioSearch(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    setActiveGamesByStudioSearch(gamesByStudioSearch);
                  }
                }}
              />
              <Button 
                variant="contained" 
                color="secondary" 
                onClick={() => setActiveGamesByStudioSearch(gamesByStudioSearch)}
              >
                Search
              </Button>
            </Box>
            <FormControlLabel
              control={
                <Switch
                  checked={gamesByStudioFilterLive}
                  onChange={(e) => setGamesByStudioFilterLive(e.target.checked)}
                />
              }
              label="Live Dealer"
            />
            <FormControlLabel
              control={
                <Switch
                  checked={gamesByStudioFilterCrash}
                  onChange={(e) => setGamesByStudioFilterCrash(e.target.checked)}
                />
              }
              label="Crash Game"
            />
          </Box>
          <Box sx={{ maxHeight: 400, overflowY: 'auto', minHeight: 400 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>Game Name</TableCell>
                  <TableCell>Live Dealer</TableCell>
                  <TableCell>Crash Game</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredGamesByStudioList.map((game) => (
                  <TableRow key={game.GameID}>
                    <TableCell>
                      <Typography
                        variant="body2"
                        sx={{
                          cursor: 'pointer',
                          fontWeight: 'bold',
                          color: 'primary.main',
                          '&:hover': { textDecoration: 'underline' }
                        }}
                        onClick={() => handleGameNameClick(game.GameID)}
                      >
                        {game.Name}
                      </Typography>
                    </TableCell>
                    <TableCell>{game.LiveDealer ? 'Yes' : 'No'}</TableCell>
                    <TableCell>{game.CrashGame ? 'Yes' : 'No'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Create Studio Dialog */}
      <Dialog open={createStudioDialogOpen} onClose={() => setCreateStudioDialogOpen(false)}>
        <DialogTitle>Create Studio</DialogTitle>
        <DialogContent>
          <TextField
            label="Studio Name"
            fullWidth
            value={newStudioName}
            onChange={(e) => setNewStudioName(e.target.value)}
            margin="dense"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateStudioDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleCreateStudio} disabled={!newStudioName}>
            Create
          </Button>
        </DialogActions>
      </Dialog>

      <CreateGameModal
        open={createGameDialogOpen}
        onClose={() => setCreateGameDialogOpen(false)}
        studios={studios}
        onGameCreated={handleGameCreated}
      />

      {/* Delete Studio Confirmation Dialog */}
      <Dialog open={deleteStudioDialogOpen} onClose={() => setDeleteStudioDialogOpen(false)}>
        <DialogTitle>Warning</DialogTitle>
        <DialogContent>
          <p>
            WARNING: There are {gamesAssociatedCount} games associated with this studio. Deleting this studio will
            mark these games as having an "unknown" studio. Do you want to continue?
          </p>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteStudioDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" color="error" onClick={confirmDeleteStudio}>
            Go Ahead
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Game Dialog */}
      <Dialog open={updateStudioDialogOpen} onClose={() => setUpdateStudioDialogOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Edit Game</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
          {editGameError && <Alert severity="error">{editGameError}</Alert>}
          <TextField
            label="Game Name"
            fullWidth
            value={updatedGameName}
            onChange={(e) => setUpdatedGameName(e.target.value)}
            margin="dense"
          />
          <Autocomplete
            options={studios}
            getOptionLabel={(option) => option.Name || ''}
            value={studios.find((studio) => studio.StudioID === updatedStudioID) || null}
            onChange={(event, newValue) => {
              setUpdatedStudioID(newValue ? newValue.StudioID : '');
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Select Studio"
                variant="outlined"
                margin="dense"
                fullWidth
              />
            )}
          />

          <Divider sx={{ my: 1 }} />

          {/* Game Logo Section */}
          <Box>
            <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
              Game Logo (4:3 aspect ratio, min 400x300px)
            </Typography>
            
            {gameLogoPreviewUrl ? (
              <Box sx={{ mb: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                  <Typography variant="caption" color="text.secondary">Preview:</Typography>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button 
                      size="small" 
                      variant={logoPreviewTheme === 'light' ? 'contained' : 'outlined'}
                      onClick={() => setLogoPreviewTheme('light')}
                    >
                      Light
                    </Button>
                    <Button 
                      size="small" 
                      variant={logoPreviewTheme === 'dark' ? 'contained' : 'outlined'}
                      onClick={() => setLogoPreviewTheme('dark')}
                    >
                      Dark
                    </Button>
                  </Box>
                </Box>
                
                {gameLogoImage ? (
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
                    <Box 
                      sx={{ 
                        width: '100%',
                        height: 225,
                        display: 'flex', 
                        justifyContent: 'center', 
                        alignItems: 'center', 
                        border: '1px solid',
                        borderColor: 'divider',
                        borderRadius: 1,
                        overflow: 'hidden',
                        backgroundColor: logoPreviewTheme === 'light' ? '#ffffff' : '#1e1e1e',
                      }}
                    >
                      <canvas 
                        ref={gameCanvasRef} 
                        width={400} 
                        height={300} 
                        style={{ width: '300px', height: '225px' }}
                      />
                    </Box>
                    <Box sx={{ width: '100%', px: 2 }}>
                      <Typography variant="caption" color="text.secondary">Zoom:</Typography>
                      <input 
                        type="range" 
                        min={Math.max(400 / gameLogoImage.width, 300 / gameLogoImage.height) * 0.5} 
                        max={3} 
                        step={0.01} 
                        value={zoom} 
                        onChange={(e) => setZoom(parseFloat(e.target.value))} 
                        style={{ width: '100%', marginBottom: '10px' }}
                      />
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Box sx={{ width: '48%' }}>
                          <Typography variant="caption" color="text.secondary">Pan X:</Typography>
                          <input 
                            type="range" 
                            min={-gameLogoImage.width} 
                            max={gameLogoImage.width} 
                            step={1} 
                            value={panX} 
                            onChange={(e) => setPanX(parseInt(e.target.value))} 
                            style={{ width: '100%' }}
                          />
                        </Box>
                        <Box sx={{ width: '48%' }}>
                          <Typography variant="caption" color="text.secondary">Pan Y:</Typography>
                          <input 
                            type="range" 
                            min={-gameLogoImage.height} 
                            max={gameLogoImage.height} 
                            step={1} 
                            value={panY} 
                            onChange={(e) => setPanY(parseInt(e.target.value))} 
                            style={{ width: '100%' }}
                          />
                        </Box>
                      </Box>
                    </Box>
                  </Box>
                ) : (
                  <Box 
                    sx={{ 
                      height: 150, 
                      display: 'flex', 
                      justifyContent: 'center', 
                      alignItems: 'center', 
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: 1,
                      p: 1,
                      backgroundColor: logoPreviewTheme === 'light' ? '#ffffff' : '#1e1e1e',
                    }}
                  >
                    <img 
                      src={gameLogoPreviewUrl} 
                      alt="Game Logo Preview" 
                      style={{ maxHeight: '100%', maxWidth: '100%', objectFit: 'contain' }} 
                    />
                  </Box>
                )}
                
                <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                  <Button 
                    color="error" 
                    size="small" 
                    variant="outlined"
                    onClick={() => {
                      setGameLogoPreviewUrl('');
                      setGameLogoImage(null);
                    }}
                  >
                    Remove Logo
                  </Button>
                </Box>
              </Box>
            ) : (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2, fontStyle: 'italic' }}>
                No game logo configured.
              </Typography>
            )}

            {/* Sighting crop selection */}
            {recentGameTiles.length > 0 && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                  Select from Sighting Crops:
                </Typography>
                {isFetchingGameLogo ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                    <CircularProgress size={24} />
                  </Box>
                ) : (
                  <Box sx={{ display: 'flex', gap: 1.5, overflowX: 'auto', p: 1, border: '1px dashed', borderColor: 'divider', borderRadius: 1 }}>
                    {recentGameTiles.map((tile, idx) => {
                      const w = tile.WidthPx || 0;
                      const h = tile.HeightPx || 0;
                      let pillColor = 'error';
                      if (w >= 400 && h >= 300) {
                        pillColor = 'success';
                      } else if (w >= 250 && h >= 180) {
                        pillColor = 'warning';
                      }

                      return (
                        <Box 
                          key={idx} 
                          sx={{ 
                            display: 'flex', 
                            flexDirection: 'column', 
                            alignItems: 'center', 
                            gap: 0.5,
                            flexShrink: 0
                          }}
                        >
                          <Box
                            onClick={async () => {
                              setIsFetchingGameLogo(true);
                              try {
                                const websitesRes = await axios.get(
                                  `${process.env.REACT_APP_API_URL}/screenshots/${tile.WebsitesDataID}`,
                                  { headers: { 'x-api-key': process.env.REACT_APP_API_KEY } }
                                );
                                const imageLocation = websitesRes.data.ImageLocation;
                                if (imageLocation) {
                                  const cropRes = await axios.post(
                                    `${process.env.REACT_APP_API_GAMES_STUDIOS_URL}/game-management/upload-crop`,
                                    { image_location: imageLocation, bounding_box: tile.BoundingBox },
                                    { headers: { 'x-api-key': process.env.REACT_APP_API_GAMES_STUDIOS_KEY } }
                                  );
                                  const cropBase64 = cropRes.data.base64 || cropRes.data.url;
                                  const img = new Image();
                                  img.src = cropBase64;
                                  img.onload = () => {
                                    setGameLogoImage(img);
                                    setGameLogoPreviewUrl(cropBase64);
                                    setIsFetchingGameLogo(false);
                                  };
                                }
                              } catch (err) {
                                console.error("Failed to load sighting crop:", err);
                                setIsFetchingGameLogo(false);
                              }
                            }}
                            sx={{
                              width: 80,
                              height: 60,
                              border: '1px solid',
                              borderColor: 'divider',
                              borderRadius: 1,
                              overflow: 'hidden',
                              cursor: 'pointer',
                              backgroundColor: '#ffffff',
                              '&:hover': {
                                borderColor: 'primary.main',
                                boxShadow: 1
                              }
                            }}
                          >
                            <SightingCropImage 
                              WebsitesDataID={tile.WebsitesDataID} 
                              BoundingBoxID={tile.BoundingBoxID} 
                              BoundingBox={tile.BoundingBox} 
                            />
                          </Box>
                          <Chip 
                            label={w && h ? `${w}x${h}` : 'Unknown'}
                            color={pillColor}
                            size="small"
                            variant="filled"
                            sx={{ 
                              fontSize: '0.65rem', 
                              height: '16px',
                              px: 0.5,
                              fontWeight: 'bold',
                              '& .MuiChip-label': { px: 0.5 }
                            }}
                          />
                        </Box>
                      );
                    })}
                  </Box>
                )}
              </Box>
            )}

            {/* Local Upload */}
            <Box sx={{ mt: 1 }}>
              <input
                type="file"
                accept="image/png, image/jpeg"
                style={{ display: 'none' }}
                id="game-logo-file-input"
                onChange={handleLocalGameLogoUpload}
              />
              <Button
                component="label"
                htmlFor="game-logo-file-input"
                variant="outlined"
                startIcon={<CloudUploadIcon />}
                fullWidth
                sx={{ height: '40px' }}
              >
                Upload Logo Image
              </Button>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUpdateStudioDialogOpen(false)} disabled={isUploadingLogo}>Cancel</Button>
          <Button variant="contained" onClick={confirmUpdateStudio} disabled={!updatedGameName.trim() || !updatedStudioID || isUploadingLogo}>
            {isUploadingLogo ? 'Saving...' : 'Save Changes'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Studio Dialog */}
      <Dialog open={editStudioDialogOpen} onClose={() => setEditStudioDialogOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Edit Studio</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 3, mt: 1 }}>
          {editStudioError && <Alert severity="error">{editStudioError}</Alert>}
          
          <TextField
            label="Studio Name"
            fullWidth
            value={updatedStudioName}
            onChange={(e) => setUpdatedStudioName(e.target.value)}
            margin="dense"
          />

          <FormControlLabel
            control={
              <Switch
                checked={isInHouse}
                onChange={(e) => {
                  setIsInHouse(e.target.checked);
                  if (!e.target.checked) {
                    setSelectedOperatorID('');
                  }
                }}
              />
            }
            label="Associate as in-house Studio"
          />

          {isInHouse && (
            <Autocomplete
              options={operators}
              getOptionLabel={(option) => option?.OperatorName || ''}
              value={operators.find((op) => op.OperatorID === selectedOperatorID) || null}
              isOptionEqualToValue={(option, value) => option.OperatorID === value?.OperatorID}
              onChange={(event, newValue) => {
                setSelectedOperatorID(newValue ? newValue.OperatorID : '');
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Select Operator"
                  variant="outlined"
                  margin="dense"
                  fullWidth
                />
              )}
            />
          )}

          <Divider />

          {/* Logo Section */}
          <Box>
            <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
              Studio Logo
            </Typography>
            
            {logoPreviewUrl ? (
              <Box sx={{ mb: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                  <Typography variant="caption" color="text.secondary">Preview:</Typography>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button 
                      size="small" 
                      variant={logoPreviewTheme === 'light' ? 'contained' : 'outlined'}
                      onClick={() => setLogoPreviewTheme('light')}
                    >
                      Light
                    </Button>
                    <Button 
                      size="small" 
                      variant={logoPreviewTheme === 'dark' ? 'contained' : 'outlined'}
                      onClick={() => setLogoPreviewTheme('dark')}
                    >
                      Dark
                    </Button>
                  </Box>
                </Box>
                <Box 
                  sx={{ 
                    height: 100, 
                    display: 'flex', 
                    justifyContent: 'center', 
                    alignItems: 'center', 
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 1,
                    p: 1,
                    backgroundColor: logoPreviewTheme === 'light' ? '#ffffff' : '#1e1e1e',
                    transition: 'background-color 0.2s ease'
                  }}
                >
                  <img 
                    src={logoPreviewUrl} 
                    alt="Logo Preview" 
                    style={{ maxHeight: '100%', maxWidth: '100%', objectFit: 'contain' }} 
                  />
                </Box>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button 
                    color="error" 
                    size="small" 
                    variant="outlined"
                    onClick={() => {
                      setLogoPreviewUrl('');
                      setLogoFileBase64('');
                    }}
                    sx={{ mt: 1 }}
                  >
                    Remove Logo
                  </Button>
                  {logoContentType !== 'image/svg+xml' && (
                    <Button 
                      color="primary" 
                      size="small" 
                      variant="outlined"
                      onClick={async () => {
                        try {
                          const transparentLogo = await makeBackgroundTransparent(logoPreviewUrl);
                          setLogoPreviewUrl(transparentLogo);
                          setLogoFileBase64(transparentLogo);
                          setLogoContentType('image/png');
                        } catch (err) {
                          console.error("Failed to remove background:", err);
                        }
                      }}
                      sx={{ mt: 1 }}
                    >
                      Make Background Transparent
                    </Button>
                  )}
                </Box>
              </Box>
            ) : (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2, fontStyle: 'italic' }}>
                No logo configured.
              </Typography>
            )}

            <Grid container spacing={2} sx={{ mt: 1 }}>
              {/* Find Online */}
              <Grid item xs={12} sm={7}>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <TextField
                    label="Website Domain"
                    placeholder="example.com"
                    size="small"
                    value={logoDomain}
                    onChange={(e) => setLogoDomain(e.target.value)}
                    fullWidth
                  />
                  <Button 
                    variant="outlined" 
                    onClick={handleFetchLogoOnline}
                    disabled={isFetchingLogo || !logoDomain.trim()}
                    sx={{ minWidth: 100 }}
                  >
                    {isFetchingLogo ? <CircularProgress size={20} /> : 'Fetch Logo'}
                  </Button>
                </Box>
                {suggestedDomains.length > 0 && (
                  <Box sx={{ mt: 1, display: 'flex', gap: 0.5, flexWrap: 'wrap', alignItems: 'center' }}>
                    <Typography variant="caption" color="text.secondary" sx={{ mr: 0.5 }}>
                      Suggested:
                    </Typography>
                    {suggestedDomains.map((domain) => (
                      <Chip
                        key={domain}
                        label={domain}
                        size="small"
                        onClick={async () => {
                          setLogoDomain(domain);
                          await fetchLogoForDomain(domain);
                        }}
                        sx={{ cursor: 'pointer', height: 20, fontSize: '0.75rem' }}
                      />
                    ))}
                  </Box>
                )}
                {logoCandidates.length > 0 && (
                  <Box sx={{ mt: 2, mb: 1 }}>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                      Discovered logo options (click to select):
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', maxHeight: 85, overflowY: 'auto', p: 0.5, border: '1px dashed', borderColor: 'divider', borderRadius: 1 }}>
                      {logoCandidates.map((url, idx) => (
                        <Box
                          key={idx}
                          onClick={() => handleSelectCandidate(url)}
                          sx={{
                            width: 50,
                            height: 50,
                            border: '1px solid',
                            borderColor: logoPreviewUrl === url ? 'primary.main' : 'divider',
                            borderRadius: 1,
                            p: 0.5,
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            backgroundColor: '#ffffff',
                            '&:hover': {
                              borderColor: 'primary.light',
                              boxShadow: 1
                            }
                          }}
                        >
                          <img 
                            src={url} 
                            alt={`Candidate ${idx+1}`} 
                            style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
                            onError={(e) => { e.target.style.display = 'none'; }}
                          />
                        </Box>
                      ))}
                    </Box>
                  </Box>
                )}
              </Grid>

              {/* Upload Local */}
              <Grid item xs={12} sm={5} sx={{ display: 'flex', alignItems: 'center' }}>
                <input
                  type="file"
                  accept="image/png, image/svg+xml"
                  style={{ display: 'none' }}
                  id="logo-file-input"
                  onChange={handleLocalLogoUpload}
                />
                <Button
                  component="label"
                  htmlFor="logo-file-input"
                  variant="outlined"
                  startIcon={<CloudUploadIcon />}
                  fullWidth
                  sx={{ height: '40px' }}
                >
                  Upload File
                </Button>
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditStudioDialogOpen(false)} disabled={isUploadingLogo}>Cancel</Button>
          <Button 
            variant="contained" 
            onClick={confirmEditStudio} 
            disabled={!updatedStudioName.trim() || isUploadingLogo}
          >
            {isUploadingLogo ? 'Saving...' : 'Save Changes'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Game Summary Dialog */}
      <Dialog
        open={summaryOpen}
        onClose={() => setSummaryOpen(false)}
        fullWidth
        maxWidth="lg"
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow: '0px 8px 32px rgba(0, 0, 0, 0.08)',
          }
        }}
      >
        <DialogTitle sx={{ m: 0, p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid', borderColor: 'divider' }}>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 'bold', letterSpacing: 0.5 }}>
              {summaryData ? summaryData.Name : 'GAME SUMMARY'}
            </Typography>
            {summaryData && (
              <Typography variant="caption" color="text.secondary">
                ID: {summaryData.GameID}
              </Typography>
            )}
          </Box>
          <IconButton onClick={() => setSummaryOpen(false)} size="small" sx={{ color: 'text.secondary' }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ p: 3, bgcolor: 'background.default' }}>
          {summaryLoading ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', py: 10, gap: 2 }}>
              <CircularProgress size={48} />
              <Typography variant="body2" color="text.secondary">Aggregating game data and rollout history...</Typography>
            </Box>
          ) : summaryError ? (
            <Alert severity="error" sx={{ my: 2 }}>{summaryError}</Alert>
          ) : summaryData ? (
            <Grid container spacing={3}>
              {/* Left Column: Key Facts & Launch Timelines */}
              <Grid item xs={12} md={5}>
                <Paper sx={{ p: 2, borderRadius: 2, mb: 2, boxShadow: 'none', border: '1px solid', borderColor: 'divider' }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 2, color: 'text.primary', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                    Key Facts
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={6}>
                      <Typography variant="caption" color="text.secondary" display="block">Studio</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>{summaryData.StudioName}</Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="caption" color="text.secondary" display="block">First Sighted</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {summaryData.FirstSighting ? new Date(summaryData.FirstSighting).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' }) : 'Never Sighted'}
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="caption" color="text.secondary" display="block">Live Dealer</Typography>
                      <Chip 
                        label={summaryData.LiveDealer ? "Yes" : "No"} 
                        size="small" 
                        color={summaryData.LiveDealer ? "success" : "default"}
                        variant={summaryData.LiveDealer ? "filled" : "outlined"}
                        sx={{ mt: 0.5 }}
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="caption" color="text.secondary" display="block">Crash Game</Typography>
                      <Chip 
                        label={summaryData.CrashGame ? "Yes" : "No"} 
                        size="small" 
                        color={summaryData.CrashGame ? "success" : "default"}
                        variant={summaryData.CrashGame ? "filled" : "outlined"}
                        sx={{ mt: 0.5 }}
                      />
                    </Grid>
                  </Grid>
                </Paper>

                <Paper sx={{ borderRadius: 2, overflow: 'hidden', boxShadow: 'none', border: '1px solid', borderColor: 'divider' }}>
                  <Tabs 
                    value={summaryTab} 
                    onChange={(e, val) => setSummaryTab(val)}
                    variant="fullWidth"
                    sx={{ borderBottom: '1px solid', borderColor: 'divider', bgcolor: 'action.hover' }}
                  >
                    <Tab label="Jurisdictions" sx={{ fontWeight: 'bold', fontSize: '0.8rem' }} />
                    <Tab label="Brands" sx={{ fontWeight: 'bold', fontSize: '0.8rem' }} />
                  </Tabs>
                  
                  <Box sx={{ p: 1, maxHeight: 220, overflowY: 'auto', minHeight: 220 }}>
                    {summaryTab === 0 ? (
                      summaryData.Jurisdictions && summaryData.Jurisdictions.length > 0 ? (
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell sx={{ fontWeight: 'bold' }}>Jurisdiction</TableCell>
                              <TableCell sx={{ fontWeight: 'bold' }}>First Launch</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {summaryData.Jurisdictions.map((j, i) => (
                              <TableRow key={i} hover>
                                <TableCell>
                                  <Chip label={j.CountryCode} size="small" color="primary" variant="outlined" sx={{ fontWeight: 'bold' }} />
                                </TableCell>
                                <TableCell variant="body2">
                                  {new Date(j.FirstSeenTimestamp).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })}
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      ) : (
                        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 180 }}>
                          <Typography variant="body2" color="text.secondary">No jurisdiction launches found.</Typography>
                        </Box>
                      )
                    ) : (
                      summaryData.Brands && summaryData.Brands.length > 0 ? (
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell sx={{ fontWeight: 'bold' }}>Brand</TableCell>
                              <TableCell sx={{ fontWeight: 'bold' }}>First Launch</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {summaryData.Brands.map((b, i) => (
                              <TableRow key={i} hover>
                                <TableCell>
                                  <Typography variant="body2" sx={{ fontWeight: 600 }}>{b.BrandName || b.BrandID}</Typography>
                                </TableCell>
                                <TableCell variant="body2">
                                  {new Date(b.FirstSeenTimestamp).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })}
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      ) : (
                        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 180 }}>
                          <Typography variant="body2" color="text.secondary">No brand rollouts found.</Typography>
                        </Box>
                      )
                    )}
                  </Box>
                </Paper>
              </Grid>

              {/* Right Column: Prominence Visualization */}
              <Grid item xs={12} md={7}>
                <Paper sx={{ p: 2, height: '100%', borderRadius: 2, boxShadow: 'none', border: '1px solid', borderColor: 'divider', display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
                  <Box>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'center', mb: 2, gap: 1 }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: 'text.primary', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                        Visual Performance History
                      </Typography>
                      
                      {/* Metric Controls */}
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <Button 
                          variant={summaryMetric === 'prominence' ? 'contained' : 'outlined'} 
                          size="small" 
                          onClick={() => setSummaryMetric('prominence')}
                          sx={{ textTransform: 'none', fontWeight: 'bold', px: 1.5 }}
                        >
                          Prominence
                        </Button>
                        <Button 
                          variant={summaryMetric === 'count' ? 'contained' : 'outlined'} 
                          size="small" 
                          onClick={() => setSummaryMetric('count')}
                          sx={{ textTransform: 'none', fontWeight: 'bold', px: 1.5 }}
                        >
                          Appearances
                        </Button>
                      </Box>
                    </Box>

                    {/* Timescale selector controls */}
                    <Box sx={{ display: 'flex', gap: 1, mb: 3 }}>
                      {[
                        { value: '7d', label: '7 Days' },
                        { value: '1m', label: '1 Month' },
                        { value: '3m', label: '3 Months' },
                        { value: 'all', label: 'All Time' }
                      ].map((t) => (
                        <Button
                          key={t.value}
                          variant={summaryTimescale === t.value ? 'contained' : 'text'}
                          color={summaryTimescale === t.value ? 'primary' : 'inherit'}
                          size="small"
                          onClick={() => setSummaryTimescale(t.value)}
                          sx={{ 
                            textTransform: 'none', 
                            fontWeight: 'bold', 
                            borderRadius: 1.5,
                            bgcolor: summaryTimescale === t.value ? 'primary.main' : 'action.hover',
                            color: summaryTimescale === t.value ? 'primary.contrastText' : 'text.primary',
                            '&:hover': {
                              bgcolor: summaryTimescale === t.value ? 'primary.dark' : 'action.selected'
                            }
                          }}
                        >
                          {t.label}
                        </Button>
                      ))}
                    </Box>
                  </Box>

                  {/* Render Custom High-Fidelity SVG Chart */}
                  <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', minHeight: 220 }}>
                    {renderTrendChart()}
                  </Box>
                </Paper>
              </Grid>
            </Grid>
          ) : null}
        </DialogContent>
        <DialogActions sx={{ p: 2, borderTop: '1px solid', borderColor: 'divider', bgcolor: 'action.hover' }}>
          <Button onClick={() => setSummaryOpen(false)} variant="contained" sx={{ fontWeight: 'bold' }}>Close</Button>
        </DialogActions>
      </Dialog>
      
      <AddToAnomalyListModal
        open={anomalyModalOpen}
        onClose={() => setAnomalyModalOpen(false)}
        gameId={anomalyGameId}
      />
    </Box>
  );
};

export default GamesStudiosPage;
